import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Camera, MapPin, ChevronLeft, ChevronRight, Upload, AlertTriangle, RefreshCw, PlusCircle, X, Loader2, Clock, Map, Eye, EyeOff, Link2 } from 'lucide-react';
import Sphere360Viewer from '../../components/Sphere360Viewer';
import SiteWalkTimeTravel from '../../components/SiteWalkTimeTravel';
import SiteWalkAiAssist from '../../components/SiteWalkAiAssist';
import FloorPlanCanvas from '../../components/sitewalk/FloorPlanCanvas';
import { coordToView } from '../../lib/sitewalkApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HOTSPOT_TYPES = ['NAVIGATION', 'NOTE', 'ISSUE', 'QUALITY', 'SAFETY', 'PROGRESS', 'DRAWING_BIM'];
const RECORD_FROM_HOTSPOT = { QUALITY: 'QUALITY', SAFETY: 'SAFETY', ISSUE: 'ISSUE' };

const STATUS_BADGE = {
  UPLOADING: 'bg-slate-100 text-slate-600', PROCESSING: 'bg-amber-100 text-amber-700',
  READY: 'bg-emerald-100 text-emerald-700', FAILED: 'bg-red-100 text-red-700',
};

export default function SiteWalkDetail() {
  const { projectId, walkId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [walk, setWalk] = useState(null);
  const [points, setPoints] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [hotspots, setHotspots] = useState([]);
  const [addMode, setAddMode] = useState(false);
  const [pendingCoord, setPendingCoord] = useState(null);
  const [hsForm, setHsForm] = useState({ type: 'NOTE', label: '', note: '', createIssue: false });
  const [uploading, setUploading] = useState(false);
  const [timeTravel, setTimeTravel] = useState(false);
  const [aiAssist, setAiAssist] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [hiddenLayers, setHiddenLayers] = useState(new Set());
  const [selectedHs, setSelectedHs] = useState(null);
  const [focusHotspotId, setFocusHotspotId] = useState(searchParams.get('hotspot'));
  const [didFocusCp, setDidFocusCp] = useState(false);

  const active = points[activeIdx];

  const loadWalk = useCallback(async () => {
    try { setWalk((await axios.get(`${API}/projects/${projectId}/site-walks/${walkId}`)).data); }
    catch (e) { toast.error('Site walk not found'); navigate(`/app/projects/${projectId}/site-walks`); }
  }, [projectId, walkId, navigate]);

  const loadPoints = useCallback(async () => {
    try { setPoints((await axios.get(`${API}/projects/${projectId}/site-walks/${walkId}/capture-points`)).data.items || []); }
    catch (e) {}
  }, [projectId, walkId]);

  useEffect(() => { loadWalk(); loadPoints(); }, [loadWalk, loadPoints]);

  const loadHotspots = useCallback(async () => {
    if (!active) { setHotspots([]); return; }
    try { setHotspots((await axios.get(`${API}/projects/${projectId}/capture-points/${active.id}/hotspots`)).data.items || []); }
    catch (e) { setHotspots([]); }
  }, [projectId, active]);
  useEffect(() => { loadHotspots(); }, [loadHotspots]);

  // Deep-link focus: ?cp=<id>&hotspot=<id> -> select the capture point (once points load).
  useEffect(() => {
    const cpId = searchParams.get('cp');
    if (cpId && !didFocusCp && points.length) {
      const i = points.findIndex((p) => p.id === cpId);
      if (i >= 0) setActiveIdx(i);
      setDidFocusCp(true);
    }
  }, [searchParams, points, didFocusCp]);

  const focusHs = focusHotspotId ? hotspots.find((h) => h.id === focusHotspotId) : null;
  const viewOverride = focusHs ? { ...coordToView(focusHs.coord, 55), nonce: Date.now() } : undefined;
  const visibleHotspots = hotspots.filter((h) => !hiddenLayers.has(h.type));
  const toggleLayer = (t) => setHiddenLayers((prev) => { const n = new Set(prev); n.has(t) ? n.delete(t) : n.add(t); return n; });

  // Poll while the active panorama is processing
  useEffect(() => {
    if (active?.panorama && ['PROCESSING', 'UPLOADING'].includes(active.panorama.processingStatus)) {
      const t = setInterval(loadPoints, 1500);
      return () => clearInterval(t);
    }
  }, [active, loadPoints]);

  const addPoint = async () => {
    try {
      await axios.post(`${API}/projects/${projectId}/site-walks/${walkId}/capture-points`, { name: `Capture Point ${points.length + 1}` });
      await loadPoints(); setActiveIdx(points.length);
      toast.success('Capture point added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const uploadPano = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f || !active) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', f);
    try {
      await axios.post(`${API}/projects/${projectId}/capture-points/${active.id}/panorama`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Panorama uploaded — processing…');
      await loadPoints();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const retry = async (panoId) => {
    try { await axios.post(`${API}/projects/${projectId}/panoramas/${panoId}/retry`); toast.info('Retrying…'); await loadPoints(); }
    catch (e) { toast.error('Retry failed'); }
  };

  const onAddHotspot = (coord) => { setPendingCoord(coord); setAddMode(false); };

  const saveHotspot = async () => {
    if (!pendingCoord || !active) return;
    try {
      await axios.post(`${API}/projects/${projectId}/capture-points/${active.id}/hotspots`, { ...hsForm, coord: pendingCoord });
      toast.success('Hotspot saved');
      setPendingCoord(null); setHsForm({ type: 'NOTE', label: '', note: '', createIssue: false });
      await loadHotspots(); await loadPoints();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const setStatus = async (status) => {
    try { await axios.post(`${API}/projects/${projectId}/site-walks/${walkId}/transition`, { status }); await loadWalk(); toast.success(`Walk ${status}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Transition not allowed'); }
  };

  if (!walk) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-400">Loading…</div>;
  const pano = active?.panorama;
  const viewerUrl = pano && pano.processingStatus === 'READY' ? `${API}/projects/${projectId}/panoramas/${pano.id}/file` : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/app/projects/${projectId}/site-walks`)} className="p-2 rounded-lg hover:bg-slate-100" data-testid="sitewalk-detail-back"><ArrowLeft className="w-5 h-5" /></button>
            <div>
              <h1 className="text-xl font-bold text-slate-800">{walk.walkNumber} · {walk.name}</h1>
              <p className="text-xs text-slate-500">{walk.status} · {walk.capturePointCount || 0} capture points · {walk.captureMethod}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {walk.status === 'DRAFT' && <button onClick={() => setStatus('IN_PROGRESS')} className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg" data-testid="sitewalk-start-btn">Start Walk</button>}
            {walk.status === 'IN_PROGRESS' && <button onClick={() => setStatus('COMPLETED')} className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded-lg" data-testid="sitewalk-finish-btn">Finish Walk</button>}
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-4">
          {/* Capture point rail */}
          <div className="lg:col-span-1 bg-white rounded-xl border border-slate-200 p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-700">Capture Points</h3>
              <button onClick={addPoint} className="text-sky-600 hover:text-sky-700" data-testid="add-capture-point-btn"><Plus className="w-4 h-4" /></button>
            </div>
            <div className="space-y-1.5" data-testid="capture-point-list">
              {points.map((p, i) => (
                <button key={p.id} onClick={() => setActiveIdx(i)} className={`w-full text-left px-2.5 py-2 rounded-lg text-xs ${i === activeIdx ? 'bg-sky-50 border border-sky-300' : 'hover:bg-slate-50 border border-transparent'}`} data-testid={`capture-point-${p.sequence}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-700">{p.sequence}. {p.name}</span>
                    {p.panorama && <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${STATUS_BADGE[p.panorama.processingStatus]}`}>{p.panorama.processingStatus}</span>}
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-0.5">
                    <span className="px-1 rounded bg-slate-100">{p.registrationType === 'FLOOR_PLAN_POSITIONED' ? 'Floor-plan positioned' : 'Context-linked'}</span>
                    {p.newerRevisionAvailable && <span className="text-amber-600 flex items-center gap-0.5" data-testid="newer-revision-flag"><AlertTriangle className="w-2.5 h-2.5" /> newer rev</span>}
                  </div>
                </button>
              ))}
              {points.length === 0 && <p className="text-[11px] text-slate-400 px-1">No capture points. Add one to begin.</p>}
            </div>
          </div>

          {/* Viewer + controls */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 p-3">
            {!active ? (
              <div className="h-96 flex items-center justify-center text-slate-400 text-sm">Add a capture point to begin.</div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="text-xs text-slate-500 flex items-center gap-1" data-testid="viewer-breadcrumb">
                    <span className="font-medium text-slate-700">{active.name}</span>
                    <span>·</span><span>{(active.capturedAt || '').slice(0, 10)}</span>
                    {active.floorPlanPosition && <span className="px-1.5 py-0.5 bg-slate-100 rounded flex items-center gap-1"><MapPin className="w-3 h-3" /> {active.floorPlanPosition.drawingNumber} rev {active.floorPlanPosition.revision}</span>}
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => setActiveIdx(Math.max(0, activeIdx - 1))} disabled={activeIdx === 0} className="p-1.5 rounded border text-slate-600 disabled:opacity-30" data-testid="viewer-prev"><ChevronLeft className="w-4 h-4" /></button>
                    <button onClick={() => setActiveIdx(Math.min(points.length - 1, activeIdx + 1))} disabled={activeIdx >= points.length - 1} className="p-1.5 rounded border text-slate-600 disabled:opacity-30" data-testid="viewer-next"><ChevronRight className="w-4 h-4" /></button>
                  </div>
                </div>

                {viewerUrl ? (
                  <>
                    <Sphere360Viewer imageUrl={viewerUrl} hotspots={visibleHotspots} addMode={addMode} onAddHotspot={onAddHotspot}
                      onSelectHotspot={(hs) => setSelectedHs(hs)} viewOverride={viewOverride} />
                    {/* Hotspot layer toggles */}
                    <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="hotspot-layers">
                      {HOTSPOT_TYPES.map((t) => {
                        const count = hotspots.filter((h) => h.type === t).length;
                        if (!count) return null;
                        const on = !hiddenLayers.has(t);
                        return (
                          <button key={t} onClick={() => toggleLayer(t)} data-testid={`layer-toggle-${t}`}
                            className={`text-[10px] px-2 py-0.5 rounded-full border flex items-center gap-1 ${on ? 'bg-slate-100 text-slate-600 border-slate-200' : 'bg-white text-slate-300 border-slate-100'}`}>
                            {on ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />} {t} ({count})
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                      <p className="text-[11px] text-slate-400">{visibleHotspots.length}/{hotspots.length} hotspot(s) shown · panorama-relative orientation</p>
                      <div className="flex gap-2 flex-wrap">
                        {active.floorPlanPosition && (
                          <button onClick={() => setShowPlan(true)} className="text-xs flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-slate-600" data-testid="show-on-plan-btn">
                            <Map className="w-3.5 h-3.5" /> Show on Plan
                          </button>
                        )}
                        <button onClick={() => setTimeTravel(true)} className="text-xs flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-slate-600" data-testid="time-travel-btn">
                          <Clock className="w-3.5 h-3.5" /> Time Travel
                        </button>
                        <button onClick={() => setAiAssist(true)} className="text-xs flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-violet-300 text-violet-700" data-testid="ai-assist-btn">
                          <PlusCircle className="w-3.5 h-3.5" /> AI Assist
                        </button>
                        <button onClick={() => setAddMode((v) => !v)} className={`text-xs flex items-center gap-1 px-2.5 py-1.5 rounded-lg ${addMode ? 'bg-blue-600 text-white' : 'border text-slate-600'}`} data-testid="toggle-add-hotspot">
                          <PlusCircle className="w-3.5 h-3.5" /> {addMode ? 'Cancel' : 'Add Hotspot'}
                        </button>
                      </div>
                    </div>
                  </>
                ) : pano ? (
                  <div className="h-96 rounded-lg bg-slate-100 flex flex-col items-center justify-center gap-2" data-testid="pano-processing-state">
                    {pano.processingStatus === 'FAILED' ? (
                      <>
                        <AlertTriangle className="w-8 h-8 text-red-500" />
                        <p className="text-sm text-red-600">Processing failed — original evidence retained</p>
                        <p className="text-[11px] text-slate-400 max-w-md text-center">{pano.processingError}</p>
                        <button onClick={() => retry(pano.id)} className="text-xs flex items-center gap-1 px-3 py-1.5 bg-slate-700 text-white rounded-lg" data-testid="pano-retry-btn"><RefreshCw className="w-3.5 h-3.5" /> Retry</button>
                      </>
                    ) : (
                      <><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /><p className="text-sm text-slate-500">Processing panorama…</p></>
                    )}
                    {pano.validationState === 'WARNING' && <p className="text-[11px] text-amber-600 max-w-md text-center">{pano.validationReason}</p>}
                  </div>
                ) : (
                  <label className="h-96 rounded-lg bg-slate-50 border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-slate-100" data-testid="pano-upload-drop">
                    {uploading ? <Loader2 className="w-8 h-8 text-sky-500 animate-spin" /> : <Upload className="w-8 h-8 text-slate-400" />}
                    <p className="text-sm text-slate-500">{uploading ? 'Uploading…' : 'Upload a 360° panorama (JPEG/PNG/WEBP/HEIC)'}</p>
                    <p className="text-[11px] text-slate-400">Equirectangular (≈2:1) recommended</p>
                    <input type="file" accept=".jpg,.jpeg,.png,.webp,.heic,.heif,image/*" className="hidden" onChange={uploadPano} data-testid="pano-file-input" />
                  </label>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Hotspot form */}
      {pendingCoord && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="hotspot-form-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold text-slate-800">New Hotspot</h3>
              <button onClick={() => setPendingCoord(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <p className="text-[11px] text-slate-400 mb-3">Panorama-relative yaw {pendingCoord.yaw.toFixed(2)}, pitch {pendingCoord.pitch.toFixed(2)} (viewer orientation, not world coordinates).</p>
            <label className="text-xs font-medium text-slate-600">Type</label>
            <select value={hsForm.type} onChange={(e) => setHsForm({ ...hsForm, type: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm mt-1 mb-3" data-testid="hotspot-type-select">
              {['NOTE', 'NAVIGATION', 'ISSUE', 'QUALITY', 'SAFETY', 'PROGRESS', 'DRAWING_BIM'].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <label className="text-xs font-medium text-slate-600">Label</label>
            <input value={hsForm.label} onChange={(e) => setHsForm({ ...hsForm, label: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm mt-1 mb-3" data-testid="hotspot-label-input" />
            <label className="text-xs font-medium text-slate-600">Note</label>
            <textarea value={hsForm.note} onChange={(e) => setHsForm({ ...hsForm, note: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm mt-1 mb-3" rows={2} data-testid="hotspot-note-input" />
            {hsForm.type === 'ISSUE' && (
              <label className="flex items-center gap-2 text-xs text-slate-600 mb-3">
                <input type="checkbox" checked={hsForm.createIssue} onChange={(e) => setHsForm({ ...hsForm, createIssue: e.target.checked })} data-testid="hotspot-create-issue" />
                Also create a linked Issue (deep-links back to this hotspot)
              </label>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setPendingCoord(null)} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
              <button onClick={saveHotspot} className="px-4 py-2 bg-sky-600 text-white rounded-lg text-sm" data-testid="hotspot-save-btn">Save Hotspot</button>
            </div>
          </div>
        </div>
      )}
      {timeTravel && active && (
        <SiteWalkTimeTravel projectId={projectId} capturePoint={active} onClose={() => setTimeTravel(false)} />
      )}
      {aiAssist && active && active.panoramaId && (
        <SiteWalkAiAssist projectId={projectId} capturePoint={active} onConfirmed={loadHotspots} onClose={() => setAiAssist(false)} />
      )}
      {showPlan && active && (
        <FloorPlanCanvas projectId={projectId} focusCapturePoint={active} onClose={() => setShowPlan(false)} />
      )}
      {selectedHs && (
        <HotspotActions projectId={projectId} hotspot={selectedHs} onClose={() => setSelectedHs(null)}
          onChanged={() => { setSelectedHs(null); loadHotspots(); }} />
      )}
    </div>
  );
}

/** Compact hotspot detail + governed actions (Open / Create Record / Link). Full workflow stays in-domain. */
function HotspotActions({ projectId, hotspot, onClose, onChanged }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const linked = hotspot.linkedRecordId;

  const createRecord = async (recordType) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/projects/${projectId}/hotspots/${hotspot.id}/create-record`, { recordType, description: hotspot.note || hotspot.label });
      toast.success(`${r.data.recordType.replace(/_/g, ' ')} created & linked`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action not permitted'); }
    setBusy(false);
  };
  const openRecord = () => {
    if (hotspot.linkedRecordType === 'issue') navigate(`/app/projects/${projectId}/issues?issueId=${hotspot.linkedRecordId}`);
    else if (hotspot.linkedRecordType === 'qualityObservation') navigate(`/app/projects/${projectId}/quality`);
    else if (hotspot.linkedRecordType === 'safetyObservation') navigate(`/app/projects/${projectId}/safety`);
    else toast.info(`Linked ${hotspot.linkedRecordType || 'record'}: ${hotspot.linkedRecordId}`);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="hotspot-actions-modal">
      <div className="bg-white rounded-xl w-full max-w-sm p-5">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-bold text-slate-800">{hotspot.label || hotspot.type}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <p className="text-[11px] text-slate-500 mb-1">Type: {hotspot.type}</p>
        {hotspot.note && <p className="text-xs text-slate-600 mb-3">{hotspot.note}</p>}
        {linked ? (
          <div data-testid="hotspot-linked">
            <p className="text-[11px] text-slate-500 mb-2">Linked to {hotspot.linkedRecordType}</p>
            <button onClick={openRecord} className="w-full px-3 py-2 bg-sky-600 text-white rounded-lg text-xs flex items-center justify-center gap-1" data-testid="hotspot-open-record"><Link2 className="w-3.5 h-3.5" /> Open Record</button>
          </div>
        ) : (
          <div>
            <p className="text-[11px] text-slate-500 mb-2">Create a governed record from this evidence (human-reviewed; no automatic NCR/risk/progress):</p>
            <div className="grid grid-cols-3 gap-2">
              {Object.keys(RECORD_FROM_HOTSPOT).map((t) => (
                <button key={t} disabled={busy} onClick={() => createRecord(t)} className="px-2 py-2 rounded-lg border text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid={`hotspot-create-${t.toLowerCase()}`}>{t}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
